import { useRef, useState, useCallback } from 'react'

interface FileData {
	id: string
	name: string
	file: File
	preview?: string
	type: 'image' | 'document' | 'other'
}

interface UseUploaderOptions {
	acceptedTypes?: {
		image?: string[]
		document?: string[]
		other?: string[]
	}
	maxSize?: number
}

interface UseUploaderReturn {
	inputRef: React.RefObject<HTMLInputElement>
	files: FileData[]
	isProcessing: boolean
	reset: () => void
	handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
	removeFile: (id: string) => void
	clearFiles: () => void
	errors: string[]
}

export const useUploader = (
	options?: UseUploaderOptions
): UseUploaderReturn => {
	const defaultOptions: UseUploaderOptions = {
		acceptedTypes: {
			image: [
				'png',
				'jpg',
				'jpeg',
				'gif',
				'bmp',
				'svg+xml',
				'webp',
				'tiff',
				'ico',
			],
			document: [],
			other: [],
		},
		maxSize: 10 * 1024 * 1024,
	}

	const config = { ...defaultOptions, ...options }

	const inputRef = useRef<HTMLInputElement>(null)
	const [files, setFiles] = useState<FileData[]>([])
	const [isProcessing, setIsProcessing] = useState(false)
	const [errors, setErrors] = useState<string[]>([])

	const getFileType = useCallback(
		(file: File): 'image' | 'document' | 'other' => {
			const mimePrefix = file.type.split('/')[0]

			if (
				mimePrefix === 'image' ||
				(config.acceptedTypes?.image &&
					config.acceptedTypes.image.some(
						(type) => file.type === `image/${type}` || file.type.includes(type)
					))
			) {
				return 'image'
			} else if (
				config.acceptedTypes?.document &&
				config.acceptedTypes.document.some(
					(type) =>
						file.type === `application/${type}` || file.type.includes(type)
				)
			) {
				return 'document'
			}
			return 'other'
		},
		[config.acceptedTypes]
	)

	const isValidFile = useCallback(
		(file: File): boolean => {
			if (config.maxSize && file.size > config.maxSize) {
				setErrors((prev) => [
					...prev,
					`El archivo ${file.name} excede el tamaño máximo permitido`,
				])
				return false
			}

			const fileType = getFileType(file)
			const extension = file.name.split('.').pop()?.toLowerCase() || ''

			if (
				fileType === 'image' &&
				config.acceptedTypes?.image &&
				!config.acceptedTypes.image.some(
					(type) =>
						file.type === `image/${type}` ||
						file.type.includes(type) ||
						extension === type
				)
			) {
				setErrors((prev) => [
					...prev,
					`El archivo ${file.name} no es un tipo de imagen válido`,
				])
				return false
			}

			return true
		},
		[config.maxSize, config.acceptedTypes, getFileType]
	)

	const generatePreview = useCallback(
		(file: File): Promise<string | undefined> => {
			return new Promise((resolve) => {
				if (!file.type.startsWith('image/')) {
					resolve(undefined)
					return
				}

				const reader = new FileReader()
				reader.onload = (e) => {
					resolve(e.target?.result as string)
				}
				reader.onerror = () => resolve(undefined)
				reader.readAsDataURL(file)
			})
		},
		[]
	)

	const reset = useCallback(() => {
		if (inputRef.current) {
			inputRef.current.value = ''
		}
	}, [])

	const removeFile = useCallback((id: string) => {
		setFiles((prev) => prev.filter((file) => file.id !== id))
	}, [])

	const clearFiles = useCallback(() => {
		setFiles([])
		reset()
	}, [reset])

	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const selectedFiles = event.target.files
			if (!selectedFiles || selectedFiles.length === 0) return

			setIsProcessing(true)
			setErrors([])

			const filesArray = Array.from(selectedFiles)
			const validFiles = filesArray.filter(isValidFile)

			const newFiles: FileData[] = await Promise.all(
				validFiles.map(async (file) => {
					const id = `${file.name}-${Date.now()}`
					const preview = await generatePreview(file)
					const fileType = getFileType(file)

					return {
						id,
						name: file.name,
						file,
						preview,
						type: fileType,
					}
				})
			)

			setFiles((prev) => [...prev, ...newFiles])

			setIsProcessing(false)
			reset()
		},
		[isValidFile, generatePreview, getFileType, reset]
	)

	return {
		inputRef,
		files,
		isProcessing,
		reset,
		handleFileChange,
		removeFile,
		clearFiles,
		errors,
	}
}
